import { routeFoot } from "@/api/orval";
import type { RoutingCity } from "@/api/orval-types";

// Both routing paths are normalized to this shape before touching the map:
// the backend proxy already returns it; the public path is mapped into it.
interface NormalizedRoute {
  code: string;
  distance: number; // meters
  geometry: any; // GeoJSON LineString
}

// undo/redo revisit earlier waypoint sets, so cache computed routes to turn
// those into instant, request-free redraws.
const routeCache = new Map<string, { geometry: any; distance: number }>();

const inBbox = ([lng, lat]: [number, number], bbox: number[]) =>
  lng >= bbox[0] && lng <= bbox[2] && lat >= bbox[1] && lat <= bbox[3];

// Whether we self-host routing for the city containing this point; if not, the
// caller uses the public routing path unchanged.
export const isSelfHostedRouting = (
  coordinate: [number, number],
  cities: RoutingCity[],
) => cities.some((c) => inBbox(coordinate, c.bbox));

const publicFootRoute = async (
  coordinateString: string,
): Promise<NormalizedRoute> => {
  const response = await fetch(
    `https://routing.openstreetmap.de/routed-foot/route/v1/foot/${coordinateString}?overview=full&geometries=geojson`,
  );
  const raw = await response.json();
  const route = raw?.routes?.[0];
  return raw?.code === "Ok" && route
    ? { code: "Ok", distance: route.distance, geometry: route.geometry }
    : { code: raw?.code ?? "Error", distance: 0, geometry: null };
};

export const getOSRMRoute = async (
  coordinates: [number, number][],
  setRouteGeometry: (geometry: any) => void,
  setTotalDistance: (distance: number) => void,
  selfHosted: boolean,
) => {
  if (coordinates.length < 2) return;

  const coordinateString = coordinates
    .map((coord) => coord.join(","))
    .join(";");
  const cacheKey = `${selfHosted ? "self" : "public"}:${coordinateString}`;

  const cached = routeCache.get(cacheKey);
  if (cached) {
    setRouteGeometry({
      type: "Feature",
      properties: {},
      geometry: cached.geometry,
    });
    setTotalDistance(cached.distance);
    return;
  }

  try {
    let route: NormalizedRoute;
    if (selfHosted) {
      // Backend returns the normalized {code, distance, geometry} directly.
      const data = await routeFoot({ coordinates: coordinateString });
      route = {
        code: data.code,
        distance: data.distance ?? 0,
        geometry: data.geometry ?? null,
      };
    } else {
      route = await publicFootRoute(coordinateString);
    }

    if (route.code === "Ok" && route.geometry) {
      routeCache.set(cacheKey, {
        geometry: route.geometry,
        distance: route.distance,
      });
      setRouteGeometry({
        type: "Feature",
        properties: {},
        geometry: route.geometry,
      });
      setTotalDistance(route.distance);
    }
  } catch (error: any) {
    if (error?.response?.status === 429) {
      console.warn("Routing rate limit reached; try again in a moment.");
    } else {
      console.error("Error fetching route:", error);
    }
  }
};
